import { useState } from "react";

const EVENT_TYPES = [
  "Birthday / Baby Shower / Quinceañera",
  "Corporate Event / Brand Activation",
  "Government / Institutional Program",
  "Holiday Party",
  "Wedding / Engagement",
  "Other",
];

type FormFields = {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  date: string;
  message: string;
};

type FormErrors = Partial<Record<keyof FormFields, string>>;

function validate(form: FormFields): FormErrors {
  const errors: FormErrors = {};

  if (!form.name.trim()) {
    errors.name = "Full name is required.";
  }

  if (!form.email.trim()) {
    errors.email = "Email address is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (form.phone.trim() && !/^\(\d{3}\) \d{3}-\d{4}$/.test(form.phone)) {
    errors.phone = "Please enter a complete 10-digit phone number.";
  }

  if (!form.eventType) {
    errors.eventType = "Please select an event type.";
  }

  if (!form.message.trim()) {
    errors.message = "Please tell us about your event.";
  }

  return errors;
}

const EMPTY_FORM: FormFields = {
  name: "",
  email: "",
  phone: "",
  eventType: "",
  date: "",
  message: "",
};

export default function ContactSection() {
  const [form, setForm] = useState<FormFields>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormFields, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { name } = e.target;
    const value = name === "phone" ? formatPhone(e.target.value) : e.target.value;
    const updated = { ...form, [name]: value };
    setForm(updated);
    // Clear error for this field as user types
    if (touched[name as keyof FormFields]) {
      const newErrors = validate(updated);
      setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormFields] }));
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const newErrors = validate(form);
    setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormFields] }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const newErrors = validate(form);
    setErrors(newErrors);
    setTouched({ name: true, email: true, phone: true, eventType: true, date: true, message: true });

    if (Object.keys(newErrors).length > 0) return;

    setSubmitError(false);

    if (process.env.NODE_ENV === "development") {
      setSubmitted(true);
      return;
    }

    try {
      const body = new URLSearchParams({
        "form-name": "contact",
        ...form,
      }).toString();
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      // Netlify Forms responds with a 302 redirect on success; fetch follows it
      // so res.ok or res.redirected both indicate a successful submission
      if (res.ok || res.redirected) {
        setSubmitted(true);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    }
  }

  return (
    <section className="bg-ink py-24 px-5 lg:px-16" id="contact">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-start">

        {/* Left — info */}
        <div>
          {/* Section label strip */}
          <div className="flex items-center gap-3 mb-5">
            <span className="font-syne text-[10px] font-bold tracking-[0.28em] uppercase text-gold/60">Get in Touch</span>
            <span className="flex-1 h-px bg-white/8" />
          </div>
          <h2
            className="font-syne font-extrabold text-white tracking-tight leading-tight"
            style={{ fontSize: 'clamp(2.2rem,3.5vw,3.5rem)' }}
          >
            Let's Build Something{" "}
            <em className="font-serif italic font-light text-violet-light">Unforgettable</em>
          </h2>
          <p className="text-white/40 font-light text-sm leading-relaxed mt-5 max-w-sm">
            Tell us about your event and we'll get back to you with a custom quote within 48 hours.
          </p>

          <ul className="mt-10 space-y-5">
            {[
              {
                label: "Email",
                value: "events@oloyeevents.com",
                href: "mailto:events@oloyeevents.com",
              },
              {
                label: "Service Area",
                value: "New York City & New Jersey",
                href: null,
              },
              {
                label: "Response Time",
                value: "48-hour quotes available",
                href: null,
              },
            ].map(({ label, value, href }) => (
              <li key={label} className="flex flex-col gap-0.5">
                <span className="font-syne text-[8.5px] font-bold tracking-[0.22em] uppercase text-gold">
                  {label}
                </span>
                {href ? (
                  <a href={href} className="text-sm text-white/55 hover:text-gold-light transition-colors">
                    {value}
                  </a>
                ) : (
                  <span className="text-sm text-white/55">{value}</span>
                )}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2 mt-10">
            {["Fully Insured", "SAM.gov Registered", "Woman-Owned", "Minority-Owned"].map((pill) => (
              <span
                key={pill}
                className="text-white/25 border border-white/8 font-syne text-[9px] font-semibold tracking-[0.12em] uppercase px-3 py-1 rounded-sm"
              >
                {pill}
              </span>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div className="bg-white/5 border border-violet/25 rounded-2xl p-11">
          {submitted ? (
            <div className="flex flex-col items-center justify-center text-center py-12 gap-4">
              <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-2xl">
                ✓
              </div>
              <h3 className="font-syne font-extrabold text-white text-2xl">Message Sent!</h3>
              <p className="text-white/50 font-light text-sm max-w-xs">
                Thanks for reaching out. We'll be in touch within 48 hours with your custom quote.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); setErrors({}); setTouched({}); }}
                className="mt-2 font-syne text-xs font-medium tracking-wider text-gold underline underline-offset-4 hover:text-gold-light transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form name="contact" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit} noValidate className="space-y-5">
              <input type="hidden" name="form-name" value="contact" />
              <p style={{ display: 'none' }}><label>Don't fill this out: <input name="bot-field" /></label></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label="Full Name"
                  name="name"
                  type="text"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.name}
                  required
                />
                <Field
                  label="Email Address"
                  name="email"
                  type="email"
                  placeholder="jane@example.com"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label="Phone (optional)"
                  name="phone"
                  type="tel"
                  placeholder="(212) 555-0100"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                />
                <Field
                  label="Event Date (optional)"
                  name="date"
                  type="date"
                  placeholder=""
                  value={form.date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              {/* Event type select */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="eventType" className="font-syne text-[9px] font-bold tracking-[0.2em] uppercase text-white/35">
                  Event Type
                </label>
                <select
                  id="eventType"
                  name="eventType"
                  value={form.eventType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`bg-white/5 border text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-gold-light transition-colors appearance-none ${
                    errors.eventType ? "border-red-500/70" : "border-white/10"
                  }`}
                >
                  <option value="" disabled>Select an event type…</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {errors.eventType && (
                  <p className="text-[11px] text-red-400 mt-0.5">{errors.eventType}</p>
                )}
              </div>

              {/* Message */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="font-syne text-[9px] font-bold tracking-[0.2em] uppercase text-white/35">
                  Tell Us About Your Event
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={4}
                  placeholder="Guest count, venue, vibe, budget range…"
                  className={`bg-white/5 border text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-gold-light transition-colors resize-none placeholder:text-white/18 ${
                    errors.message ? "border-red-500/70" : "border-white/10"
                  }`}
                />
                {errors.message && (
                  <p className="text-[11px] text-red-400 mt-0.5">{errors.message}</p>
                )}
              </div>

              {submitError && (
                <p className="text-[11px] text-red-400 text-center">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}

              <button
                type="submit"
                className="bg-gold hover:bg-gold-light text-ink font-syne font-bold tracking-[0.2em] uppercase rounded-md w-full py-3.5 transition-colors"
              >
                Request a Quote
              </button>

              <p className="text-center text-white/18 text-xs">
                We respond within 48 hours · No commitment required
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  required,
}: {
  label: string;
  name: string;
  type: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={name} className="font-syne text-[9px] font-bold tracking-[0.2em] uppercase text-white/35">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        className={`bg-white/5 border text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-gold-light transition-colors placeholder:text-white/18 ${
          error ? "border-red-500/70" : "border-white/10"
        }`}
      />
      {error && <p className="text-[11px] text-red-400 mt-0.5">{error}</p>}
    </div>
  );
}
